import type { z } from 'zod';
import { skipToken, useQuery } from '@tanstack/react-query';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/smart-form/api-schema';
import { FORM_SYS_NAME } from '@/consts/system-name';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { smartFormApi } from '@/services';
import { encounterTypeSchema } from '@/types/encounter-type-schema';
import { queryEncounterCardResSchema } from '~/generated/smart-form/zod-schema';

const PATH_VAR = '{encounterId}';
const API_PATH = `/v1/encounter/queryEncounterCard/${PATH_VAR}` as const;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

const encounterCardSchema = zodRemoveNullable(queryEncounterCardResSchema, {
  includeMask: {
    id: true,
    visitStartDatetime: true,
  },
}).extend({
  encounterType: encounterTypeSchema,
});

export type QueryEncounterCardData = z.infer<typeof encounterCardSchema>;

const queryFn = async (encounterId: string, signal: AbortSignal) => {
  const { data: response } = await smartFormApi.get<APIResponse>(
    API_PATH.replace(PATH_VAR, encounterId),
    { signal },
  );

  return encounterCardSchema.parse(response.data);
};

export const useQueryEncounterCard = (
  encounterId?: string,
  options?: QueryOptions<QueryEncounterCardData>,
) => {
  return useQuery({
    queryKey: [API_PATH, FORM_SYS_NAME, encounterId],
    queryFn: encounterId
      ? ({ signal }) => queryFn(encounterId, signal)
      : skipToken,
    ...options,
  });
};
